import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  lazy,
  Suspense,
  startTransition,
} from "react";
import "./admin.css";
import perfilIcon from "../../../assets/imgs/dashboard-admin/logo-horus.png";
import conexiones_i from "../../../assets/imgs/navbar/conexiones.svg";
import camara_i from "../../../assets/imgs/navbar/icon.svg";
import gestion_i from "../../../assets/imgs/navbar/gestion.svg";
import domestico_i from "../../../assets/imgs/navbar/domestico.svg";
import { Tooltip } from "react-tooltip";
import {
  FaTimes,
  FaSignOutAlt,
  FaBars,
  FaPlaneDeparture,
  FaPlaneArrival,
} from "react-icons/fa";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import Loading from "../components/loading/loading";
import MainBanner from "../../../components/dashboard/main-banner/main-banner";

import { fetchAdminPermissions, loginAdminUser } from "./adminAccess";
import { getGestionDeAlmacenMenu as getMenuExportacion } from "./menu_redisenio/exportacion";
import { getGestionDeAlmacenMenu as getMenuImportacion } from "./menu_redisenio/importacion";
import { getGestionDeAlmacenMenu as getMenuConexiones } from "./menu_redisenio/conexiones";
import { getGestionDeAlmacenMenu as getMenuGestiones } from "./menu_redisenio/gestion";
import { getGestionDeAlmacenMenu as getMenuCamara } from "./menu_redisenio/camara";
import { getGestionDeAlmacenMenu as getMenuDomestico } from "./menu_redisenio/domestico";
import { redirectComponent } from "../../../services/redirect-components";
import {
  DASHBOARD_STORAGE_KEYS,
  clearDashboardSession,
  isBackendLoginValidationEnabled,
  isMenuPermissionFilterEnabled,
} from "../accessControl";

// Componentes lazy loaded
const MenuImportacion = lazy(() => import("./menu_redisenio/importacion"));
const MenuExportacion = lazy(() => import("./menu_redisenio/exportacion"));
const MenuConexiones = lazy(() => import("./menu_redisenio/conexiones"));
const MenuGestion = lazy(() => import("./menu_redisenio/gestion"));
const MenuCamaraFria = lazy(() => import("./menu_redisenio/camara"));
const MenuDomestico = lazy(() => import("./menu_redisenio/domestico"));

const allTopIcons = [
  {
    key: "importacion",
    icon: <FaPlaneArrival className="fs-5 text-white" />,
    tooltip: "Importación",
  },
  {
    key: "conexiones",
    icon: <img src={conexiones_i} alt="" />,
    tooltip: "Conexiones",
  },
  {
    key: "camara_fria",
    icon: <img src={camara_i} alt="" />,
    tooltip: "Cámara Fría",
  },
  {
    key: "exportacion",
    icon: <FaPlaneDeparture className="fs-5 text-white" />,
    tooltip: "Exportación",
  },
  {
    key: "gestion_de_almacen",
    icon: <img src={gestion_i} alt="" />,
    tooltip: "Gestión de Almacén",
  },
  {
    key: "domestico",
    icon: <img src={domestico_i} alt="" />,
    tooltip: "Doméstico",
  },
];

// Mapa de componentes
const componentMap = {
  // Menús principales
  MenuImportacion,
  MenuExportacion,
  MenuConexiones,
  MenuGestion,
  MenuCamaraFria,
  MenuDomestico,

  // Resto de componentes
  Usuarios: lazy(() => import("./usuarios/usuarios")),
  Permisos: lazy(() => import("./permisos/permisos")),
  Tarimas: lazy(() => import("./tarimas/tarimas")),
  RemitentesConsignatarios: lazy(
    () => import("./remitentes-consignatarios/remitentes-consignatarios"),
  ),
  LiftTrucks: lazy(() => import("./lift-trucks/lift-trucks")),
  CoolerUsers: lazy(() => import("./cooler_users/cooler_users")),
  Warehouses: lazy(() => import("./iwarehouses/iwarehouses")),
  PaymentConditions: lazy(
    () => import("./payment-conditions/payment-conditions"),
  ),
  PaymentTypes: lazy(() => import("./payment-types/payment-types")),
  Agents: lazy(() => import("./agents/agents")),
  Institutions: lazy(() => import("./institutions/institutions")),
  ClientsExpo: lazy(() => import("./clients-expo/clients_expo")),
  Irregularity: lazy(() => import("./irregularity_types/irregularity_types")),
  CFDITypes: lazy(() => import("./cfdi-types/cfdi-types")),
  Clients: lazy(() => import("./clients/clients")),
  Users: lazy(() => import("./users/users")),
  PasswordExp: lazy(() => import("./password-exp/passwordExp")),
  PerfilesUsuario: lazy(() => import("./users_profile/users_profile")),
  Capture: lazy(() => import("./export/capture/capture")),
  Assignment: lazy(() => import("./export/assignment/assignment")),
  Exp_Irregularities_sat: lazy(
    () => import("./export/irregularities_sat/irregularities_sat"),
  ),
  UPD: lazy(() => import("./export/upd-m-h/upd")),
  Entries: lazy(() => import("./export/entries/entries")),
  Billing: lazy(() => import("./export/invoice")),
  Auditor: lazy(() => import("./export/auditor/auditor")),
  ACAS: lazy(() => import("./export/acas/acas-main")),
  Acceptance: lazy(() => import("./export/acceptance/acceptance")),
  Departures: lazy(() => import("./export/departures/departures")),
  Withdrawals: lazy(() => import("./export/withdrawals/withdrawals")),
  Lags: lazy(() => import("./export/lags/lags")),
  Incidents: lazy(() => import("./incidents/incidents")),
  Milestones: lazy(() => import("./milestones/milestones")),
  AddExtras: lazy(() => import("./add-extras/add-extras")),
  KardexTransits: lazy(() => import("./kardex-transits/kardex-transits")),
  CheckInFlights: lazy(() => import("./checkIn-flights/checkin-flights")),
  FactAdmin: lazy(() => import("./export/fact_admin/fact_admin_main")),
  KardexExpo: lazy(() => import("./others/kardex_expo/kardex_expo")),
  Quoter: lazy(() => import("./others/quoter/quoter")),
  Exceptions: lazy(() => import("./others/exceptions/exceptions")),
  BatchBilling: lazy(() => import("./others/batch_billing/batch_billing")),
  BatchCancellation: lazy(
    () => import("./others/batch_cancellation/batch_cancellation"),
  ),
  TradingRules: lazy(() => import("./others/trading-rules/trading-rules")),
  ULDS: lazy(() => import("./ulds/ulds")),
  ReportTransit: lazy(() => import("./repor_transit/report_transit")),
  PinLift: lazy(() => import("./pin-lift/pin-lift")),
  Quote: lazy(() => import("./export/quote/quote")),
  FlightRecords: lazy(
    () => import("./import/entries/flight-records/flight-records"),
  ),
  Arrivals: lazy(() => import("./import/entries/arrivals/arrivals")),
  ExternalDesWarehouses: lazy(
    () => import("./external_des_warehouses/external_des_warehouses"),
  ),
  ValeCooler: lazy(() => import("./cooler/vale-cooler/valeCooler")),
  DashboardCooler: lazy(
    () => import("./cooler/dashboard-cooler/dashboard-cooler"),
  ),
  TradeRules: lazy(() => import("./export/trade_rules/trade_rules")),
  BatchFat: lazy(() => import("./export/batch_fat/batch_fat")),
  Previous: lazy(() => import("./import/previous")),
  AltaDesco: lazy(() => import("./import/billingSupervision/altaDesco")),
  DocumentAvail: lazy(
    () =>
      import("./import/billingSupervision/document-availability/document-availability"),
  ),
  FactAdminImpo: lazy(
    () =>
      import(
        "./import/billingSupervision/administrative-billing/administrative-billing"
      ),
  ),
  Manifiesto: lazy(() => import("./import/entries/manifiesto/manifiesto")),
  Master: lazy(() => import("./import/entries/master/master")),
   MultipleInvoices: lazy (() => import("./import/invoices/multiple-invoices/multiple-invoices")),
  ExchangeRate: lazy(() => import("./others/exchangeRate/exchangeRate")),
  ImpoTariff: lazy(() => import("./others/impo_tariff/impoTariff")),
  Inventario: lazy(() => import("./import/entries/inventario/inventario")),
  ExpoTariff: lazy(() => import("./others/expo_tariff/expoTariff")),
  Upd: lazy(() => import("./import/incidents/upd/upd")),
  AltasTransitos: lazy(() => import("./altas_transitos/altasTransitos")),
  IrregularidadesSat: lazy(
    () => import("./import/incidents/irregularidadesSat/irregularidadesSat"),
  ),
  Locations: lazy(() => import("./import/locations")),
  ImpoDepartures: lazy(() => import("./import/departures/departures")),
  ExceptionsImpo: lazy(() => import("./others/exceptionsImpo/exceptionsImpo")),
  PreInvoices: lazy(
    () => import("./import/invoices/pre-invoices/pre-invoices"),
  ),
  ConectividadSat: lazy(() => import("./others/conectividad_sat/ConectividadSat")),
  MaintenanceWindow: lazy(() => import("./others/maintenance_window/maintenanceWindow")),
  FTPCubiscan: lazy(() => import("./others/ftpCubiscan/ftpCubiscan")),
  LogsOracle: lazy(() => import("./others/logsOracle/logsOracle")),
  ErrorLogs: lazy(() => import("./others/errorLogs/errorLogs")),
  Services: lazy(() => import("./notificacions/services")),
  SLA: lazy(() => import("./notificacions/sla")),
  Listing: lazy(() => import("./abandonments/listing/listing")),
  Acuses: lazy(() => import("./abandonments/acuses/acuses")),
  ClasificacionSat: lazy(
    () => import("./abandonments/clasificacionSat/clasificacionSat"),
  ),
  Previos: lazy(() => import("./abandonments/previos/previos")),
  TiemposExpo: lazy(() => import("./cooler/tiempos-expo/TiemposExpo")),
  Salidas: lazy(() => import("./abandonments/salidas/salidas")),
  CalculoAbandonos: lazy(() => import("./abandonments/calculoAbandonos/calculoAbandonos")),
};

const MODULE_CONFIG = {
  importacion: {
    label: "Importación",
    getMenu: getMenuImportacion,
    component: MenuImportacion,
  },
  conexiones: {
    label: "Conexiones",
    getMenu: getMenuConexiones,
    component: MenuConexiones,
  },
  camara_fria: {
    label: "Cámara Fría",
    getMenu: getMenuCamara,
    component: MenuCamaraFria,
  },
  exportacion: {
    label: "Exportación",
    getMenu: getMenuExportacion,
    component: MenuExportacion,
  },
  gestion_de_almacen: {
    label: "Gestión de Almacén",
    getMenu: getMenuGestiones,
    component: MenuGestion,
  },
  domestico: {
    label: "Doméstico",
    getMenu: getMenuDomestico,
    component: MenuDomestico,
  },
};

const LOWERCASE_PATH_WORDS = ["de", "la", "el", "y", "a"];

const getMenuArr = (module) => MODULE_CONFIG[module]?.getMenu?.() ?? [];

const findActivePathByView = (view, menu) => {
  for (const item of menu) {
    if (item.enlace === view) {
      return item.activo;
    }

    if (item.hijos?.length) {
      const childPath = findActivePathByView(view, item.hijos);

      if (childPath) {
        return childPath;
      }
    }
  }

  return "";
};

const normalizePathForDisplay = (pathString) => {
  if (!pathString) return "";

  return pathString
    .replace(/_/g, " ")
    .split("/")
    .map((segment) =>
      segment
        .split(" ")
        .map((word, index) =>
          LOWERCASE_PATH_WORDS.includes(word.toLowerCase()) && index > 0
            ? word.toLowerCase()
            : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(" "),
    )
    .join(" / ");
};

const getWelcomeText = (activeIcon) => {
  if (!activeIcon) {
    return `¡Bienvenido a Horus!`;
  }

  return MODULE_CONFIG[activeIcon]?.label ?? "Otros";
};

const RenderPathSegments = ({ pathString }) => {
  if (!pathString) return null;

  const segments = pathString.split(" / ");

  return (
    <div className="path-segment-container d-flex align-items-center">
      {segments.map((segment, index) => (
        <React.Fragment key={index}>
          <span
            className={
              index === segments.length - 1
                ? "path-last-segment"
                : "path-segment-item"
            }
          >
            {segment}
          </span>
          {index < segments.length - 1 && (
            <span className="path-separator mx-1">/</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const ContentLoader = () => (
  <div
    className="content-loader d-flex align-items-center justify-content-center"
    style={{ minHeight: "400px" }}
  >
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Cargando...</span>
    </div>
  </div>
);

function Admin() {
  const { user, isAuthenticated, isLoading, logout, loginWithRedirect } =
    useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const shouldValidateMenuPermissions = isMenuPermissionFilterEnabled();
  const shouldValidateBackendLogin = isBackendLoginValidationEnabled();
  const hasStoredBackendSession = () =>
    Boolean(
      sessionStorage.getItem(DASHBOARD_STORAGE_KEYS.token) &&
        localStorage.getItem(DASHBOARD_STORAGE_KEYS.user),
    );
  const [backendSessionReady, setBackendSessionReady] = useState(
    hasStoredBackendSession(),
  );
  const [selectedComponent, setSelectedComponent] = useState("");
  const [click, setClick] = useState(false);
  const [activePath, setActivePath] = useState("");
  const [activeIcon, setActiveIcon] = useState(null);
  const [gestionOpenSignal, setGestionOpenSignal] = useState(0);
  const [cubiscanStatus, setCubiscanStatus] = useState(0);
  const [permissionsLoaded, setPermissionsLoaded] = useState(
    !shouldValidateMenuPermissions,
  );

  useEffect(() => {
    const path = window.location.search;
    sessionStorage.setItem("currentUrl", path);
  }, []);

  useEffect(() => {
    if (!shouldValidateBackendLogin || isLoading) {
      return;
    }

    const token = sessionStorage.getItem(DASHBOARD_STORAGE_KEYS.token);

    if (!isAuthenticated || !token) {
      loginWithRedirect();
    }
  }, [
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    shouldValidateBackendLogin,
  ]);

  const fetchCubiscanStatus = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_URL_BACKEND_EXPO}/api/v1/exp_status_cubiscan`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem(DASHBOARD_STORAGE_KEYS.token)}`,
          },
        },
      );
      res.data?.code === 1 && setCubiscanStatus(res.data.data.status);
    } catch (error) {
      console.error("Error fetching Cubiscan status:", error);
    }
  }, []);

  useEffect(() => {
    if (activeIcon === "gestion_de_almacen") {
      setGestionOpenSignal((s) => s + 1);
      fetchCubiscanStatus();
    }
  }, [activeIcon, fetchCubiscanStatus]);

  useEffect(() => {
    if (
      backendSessionReady
    ) {
      return;
    }

    let isMounted = true;

    const syncBackendSession = async () => {
      if (hasStoredBackendSession()) {
        if (isMounted) {
          setBackendSessionReady(true);
        }
        return;
      }

      if (!user || !isAuthenticated) {
        return;
      }
      const loginSucceeded = await loginAdminUser(user, navigate);

      if (isMounted && loginSucceeded) {
        setBackendSessionReady(true);
      }
    };

    syncBackendSession();

    return () => {
      isMounted = false;
    };
  }, [
    backendSessionReady,
    isAuthenticated,
    user,
  ]);

  const logoutAdmin = () => {
    clearDashboardSession();

    logout({
      logoutParams: {
        returnTo: process.env.REACT_APP_LOGOUT_URL,
      },
    });
  };

  useEffect(() => {
    window.addEventListener("force-logout", logoutAdmin);

    return () => {
      window.removeEventListener("force-logout", logoutAdmin);
    };
  }, []);

  useEffect(() => {
    if (!shouldValidateMenuPermissions || !backendSessionReady) {
      return;
    }

    let isMounted = true;

    const loadPermissions = async () => {
      setPermissionsLoaded(false);

      try {
        await fetchAdminPermissions();
      } catch (error) {
        console.error("Error fetching admin permissions:", error);
      } finally {
        if (isMounted) {
          setPermissionsLoaded(true);
        }
      }
    };

    loadPermissions();

    return () => {
      isMounted = false;
    };
  }, [
    backendSessionReady,
    location.key,
    location.pathname,
    location.search,
    shouldValidateMenuPermissions,
  ]);

  useEffect(() => {
    if (!permissionsLoaded) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const module = params.get("module");
    const view = params.get("view");

    sessionStorage.setItem("currentModule", view !== "#" ? view : "");
    setActiveIcon(module);

    const menuArr = getMenuArr(module);
    const nextActivePath = findActivePathByView(view, menuArr);
    const subModule = nextActivePath.split("/")[1] ?? "";

    sessionStorage.setItem("sub-module", subModule);
    handleComponentChange(view, nextActivePath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionsLoaded]);

  const topIcons = useMemo(() => {
    if (!permissionsLoaded && shouldValidateMenuPermissions) {
      return [];
    }

    return allTopIcons.filter((item) => getMenuArr(item.key).length > 0);
  }, [permissionsLoaded, shouldValidateMenuPermissions]);

  useEffect(() => {
    if (!permissionsLoaded) {
      return;
    }

    if (activeIcon && !topIcons.some((item) => item.key === activeIcon)) {
      setActiveIcon(null);
      setSelectedComponent("");
      setActivePath("");
    }
  }, [activeIcon, permissionsLoaded, topIcons]);

  const sectionNames = {
    Usuarios: "Usuarios",
    Permisos: "Permisos",
    Tarimas: "Tarimas",
    CoolerUsers: "Usuarios Cooler",
    ExternalDesWarehouses: "Almacenes externos destino",
    Agents: "Agentes",
    PerfilesUsuario: "Perfil de Usuarios",
    RemitentesConsignatarios: "Remitentes/Consignatarios",
    LiftTrucks: "Montacarguistas",
    PaymentConditions: "Condiciones de pago",
    PaymentTypes: "Tipos de pago",
    Irregularity: "Irregularidades",
    CFDITypes: "Tipos de CFDI",
    Institutions: "Instituciones",
    Clients: "Clientes",
    ClientsExpo: "Clientes Expo",
    Users: "Usuarios",
    PasswordExp: "Exp. Contraseñas",
    Incidents: "Incidencias",
    Capture: "Captura",
    Assignment: "Asignación",
    Exp_Irregularities_sat: "Irregularidades SAT Expo",
    UPD: "UPD",
    Entries: "Entradas",
    Billing: "Pre-facturación",
    Auditor: "Auditor",
    FactAdmin: "Facturación Administrativa",
    ACAS: "ACAS",
    Acceptance: "Aceptación",
    Departures: "Salidas",
    Withdrawals: "Desistimientos",
    Lags: "Rezagos",
    KardexExpo: "Kárdex Exportación",
    Quoter: "Cotizador",
    Exceptions: "Excepciones",
    BatchBilling: "Batch de facturas no timbradas",
    BatchCancellation:
      "Batch de facturas canceladas en espera de UUID referenciado",
    TradingRules: "Reglas Comerciales",
    Milestones: "Milestones",
    AddExtras: "Altas Tránsitos",
    KardexTransits: "Kárdex Tránsitos",
    CheckInFlights: "Check In",
    Report_transit: "Tránsitos",
    ULDS: "ULD's",
    ValeCooler: "Cooler",
    DashboardCooler: "Dashboard Cooler",
    FlightRecords: "Registro Vuelo",
    Arrivals: "Arribos",
    Warehouses: "Almacenes",
    Quote: "Cotización",
    TradeRules: "Reglas Comerciales",
    BatchFat: "Batch FAT",
    Previous: "Almacén",
    AltaDesco: "Alta desco",
    DocumentAvail: "Dashboard disponibilidad de documentos",
    ExchangeRate: "Tipo de Cambio",
    ImpoTariff: "Tarifario IMPO",
    AltasTransitos: "Altas Tránsitos",
    Locations: "Almacén",
    PreInvoices: "Pre-facturación",
    ConectividadSat: "Conectividad Sat",
    FactAdminImpo: "Facturación Administrativa",
    MaintenanceWindow: "Ventana de Mantenimiento",
    FTPCubiscan: "FTP Cubiscan",
    LogsOracle: "Logs Oracle",
    ErrorLogs: "Error Logs",
    Services: "Servicios",
    SLA: "SLA",
    Listing: "Listado de Abandonos",
    Acuses: "Acuse recibido SAT",
    ClasificacionSat: "Clasificación SAT",
    Previos: "Previos de abandono",
    ImpoDepartures: "Salidas",
    TiemposExpo: 'Tiempos Exportación',
    Salidas: 'Salidas',
    CalculoAbandonos: 'Reporteador'
  };

  const changeLaravelView = (view) => {
    let componentName = "";
    const parentView = view.split("/")[1];
    const viewChild = view.split("/")[2];
    const parentViewChild = view.split("/")[3];

    switch (view.split("/")[0]) {
      case "importación":
        componentName = "Importacion";
        break;
      case "conexiones":
        componentName = "Conexiones";
        break;
      case "cámara_fría":
        componentName = "Camara_fria";
        break;
      case "exportación":
        componentName = "Exportacion";
        break;
      case "gestión_de_almacén":
        componentName = "Almacen";
        break;
      case "doméstico":
        componentName = "Domestico";
        break;
      default:
        componentName = "";
        break;
    }

    if (!parentView && !viewChild) {
      return;
    }

    if (!redirectComponent[componentName]) return;
    if (!redirectComponent[componentName][parentView]) return;

    if (!view.split("/")[2]) {
      console.info(
        "url-redirect-child: ",
        redirectComponent[componentName][parentView].activo,
      );

      window.location.href = `${process.env.REACT_APP_URL_TO_LARAVEL}?destino=${redirectComponent[componentName][parentView].activo}`;
      return;
    }

    if (!redirectComponent[componentName][parentView][viewChild]) return;

    if (!view.split("/")[3]) {
      console.info(
        "url-redirect-child2: ",
        redirectComponent[componentName][parentView][viewChild].activo,
      );

      window.location.href = `${process.env.REACT_APP_URL_TO_LARAVEL}?destino=${redirectComponent[componentName][parentView][viewChild].activo}`;
      return;
    }

    if (
      !redirectComponent[componentName][parentView][viewChild][parentViewChild]
    )
      return;

    console.info(
      "url-redirect: ",
      redirectComponent[componentName][parentView][viewChild][parentViewChild]
        .activo,
    );

    window.location.href = `${process.env.REACT_APP_URL_TO_LARAVEL}?destino=${redirectComponent[componentName][parentView][viewChild][parentViewChild].activo}`;
  };

  const handleComponentChange = (componentName, newActivePath) => {
    if (componentName === "#") {
      changeLaravelView(newActivePath);
    }

    startTransition(() => {
      setSelectedComponent(componentName);
      setActivePath(normalizePathForDisplay(newActivePath));
    });
  };

  const handleIconClick = (iconKey) => {
    startTransition(() => {
      setActiveIcon((currentIcon) =>
        currentIcon === iconKey ? null : iconKey,
      );
    });
  };

  if (shouldValidateBackendLogin && (isLoading || !backendSessionReady)) {
    return <Loading />;
  }

  if (shouldValidateBackendLogin && !isAuthenticated) {
    return null;
  }

  const welcomeText = getWelcomeText(activeIcon);
  const SelectedComponent = componentMap[selectedComponent];

  const renderMenuComponent = () => {
    if (!permissionsLoaded) {
      return <ContentLoader />;
    }

    const ActiveMenuComponent = MODULE_CONFIG[activeIcon]?.component;

    if (!ActiveMenuComponent) {
      return null;
    }

    if (activeIcon === "gestion_de_almacen") {
      return (
        <ActiveMenuComponent
          activePath={activePath}
          onComponentChange={handleComponentChange}
          openSignal={gestionOpenSignal}
          openParam={cubiscanStatus}
          onCubiscanRequest={() => {
            setGestionOpenSignal((signal) => signal + 1);
            fetchCubiscanStatus();
          }}
        />
      );
    }

    return (
      <ActiveMenuComponent
        activePath={activePath}
        onComponentChange={handleComponentChange}
      />
    );
  };

  return (
    <div className="container-fluid p-0 m-0">
      <MainBanner />

      <div className="welcome-bar">
        <p>{welcomeText}</p>
      </div>

      <div className="d-flex">
        <div className="admin-menu">
          <div
            className="menu-icon-open-close"
            onClick={() => setClick((isOpen) => !isOpen)}
          >
            {click ? <FaTimes size={28} /> : <FaBars size={28} />}
          </div>

          <div className={click ? "menu-admin active" : "menu-admin"}>
            <div className="d-flex flex-column">
              <div className="d-flex justify-content-center">
                <img className="admin-profile-img" src={perfilIcon} alt="" />
              </div>
              <div className="d-flex justify-content-center">
                <p>
                  <font size="1" className="text-white">
                    V. 1.0
                  </font>
                </p>
              </div>
            </div>

            <div className="d-flex">
              {topIcons.map((item) => (
                <div className="d-flex align-items-center mx-1" key={item.key}>
                  <div
                    className={`${topIcons.length < 6 ? "max-circle" : "min-circle"} icon-circle d-flex align-items-center justify-content-center mb-1 ${
                      activeIcon === item.key ? "active" : ""
                    }`}
                    onClick={() => handleIconClick(item.key)}
                    data-tooltip-id={`importacion-tip-${item.key}`}
                    data-tooltip-content={item.tooltip}
                    data-tooltip-place="Top"
                  >
                    {item.icon}
                  </div>
                  <Tooltip
                    id={`importacion-tip-${item.key}`}
                    className="dark-tooltip"
                  />
                </div>
              ))}
            </div>

            <div className="sidebar-scroll">{renderMenuComponent()}</div>

            <div className="logout-container">
              <button className="button-logout" onClick={logoutAdmin}>
                <div className="text-menu-item-logout text-center w-100">
                  <FaSignOutAlt className="fs-5" />
                  <p className="ps-2 m-0">
                    <b>Cerrar sesión</b>
                  </p>
                </div>
              </button>
            </div>
          </div>
        </div>

        <div className="col-md-12">
          <div className="main-content flex-grow-1">
            {selectedComponent === "" ? (
              <div className="menu-item-content"></div>
            ) : (
              <div className="menu-item-content-vistas">
                <div className="row mb-3">
                  {activeIcon && selectedComponent && (
                    <div className="breadcrumb-bar px-3 py-2">
                      <small>
                        <RenderPathSegments pathString={activePath} />
                      </small>
                    </div>
                  )}
                </div>
                <div className="main-panel px-2 py-2">
                  <h4>{sectionNames[selectedComponent] || ""}</h4>
                  <div className="main-panel-body">
                    <Suspense fallback={<ContentLoader />}>
                      {SelectedComponent ? <SelectedComponent /> : ""}
                    </Suspense>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Admin;
